import { useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  checkTankValue,
  type TankValueCheck,
  type TankValueCheckResult,
} from "src/components/pages/Settings/YachtSetUp/TankPlans/tankLimits.utils";
import { useTankPlans } from "src/hooks/useTankPlans";

import { resolveTargetId } from "./useCopyVal";

type Params = {
  tankRef?: string;
  tankChecks?: TankValueCheck[];
  rowCols?: Record<string, any>;
  fieldName: string;
};

const resolveTankFieldUuid = (
  tankRef: string | undefined,
  rowCols: Record<string, any> | undefined,
): string | undefined => {
  if (!tankRef || !rowCols) return undefined;
  if (rowCols[tankRef]) return tankRef;
  return resolveTargetId(tankRef, rowCols);
};

const extractTankId = (tankFormValue: unknown): string | undefined => {
  if (!tankFormValue || typeof tankFormValue !== "object") return undefined;
  const v = tankFormValue as {
    key?: string;
    data?: { tankid?: string };
  };
  if (v.data?.tankid) return v.data.tankid;
  if (v.key) return v.key;
  return undefined;
};

export const useTankValueValidator = ({
  tankRef,
  tankChecks,
  rowCols,
  fieldName,
}: Params): {
  enabled: boolean;
  result: TankValueCheckResult;
  customValidator: (v: unknown) => boolean;
} => {
  const effectiveTankRef = tankRef;

  const enabled = Boolean(effectiveTankRef);
  const { control, trigger } = useFormContext();
  const { getTank } = useTankPlans({ enabled });

  const tankFieldUuid = useMemo(
    () => resolveTankFieldUuid(effectiveTankRef, rowCols),
    [effectiveTankRef, rowCols],
  );

  const checks: TankValueCheck[] = useMemo(
    () => tankChecks ?? (effectiveTankRef ? ["limits", "capacity"] : []),
    [tankChecks, effectiveTankRef],
  );

  const tankFormValue = useWatch({
    control,
    name: tankFieldUuid ?? "__tank_ref_unused__",
  });

  const orbTankId = useWatch({
    control,
    name: tankFieldUuid
      ? `${tankFieldUuid}.data.tankid`
      : "__tank_ref_unused_orb__",
  });

  const tankid = useMemo(() => {
    if (!tankFieldUuid) return undefined;
    if (typeof orbTankId === "string" && orbTankId) return orbTankId;
    return extractTankId(tankFormValue);
  }, [tankFieldUuid, orbTankId, tankFormValue]);

  const tank = getTank(tankid);

  const quantityValue = useWatch({
    control,
    name: fieldName || "__tank_qty_unused__",
  });

  const result = useMemo(
    () =>
      enabled
        ? checkTankValue(quantityValue, tank, checks)
        : ({ level: "ok" } as TankValueCheckResult),
    [enabled, quantityValue, tank, checks],
  );

  // Re-validate only when the selected tank changes — not on every keystroke.
  // Live UI uses `result`; save still runs trigger() over active fields.
  useEffect(() => {
    if (!enabled || !fieldName || !tankid) return;
    void trigger(fieldName);
  }, [enabled, tankid, fieldName, trigger]);

  const customValidator = useCallback(
    (v: unknown) =>
      checkTankValue(v as string | number | null | undefined, tank, checks)
        .level !== "error",
    [tank, checks],
  );

  return { enabled, result, customValidator };
};
